import React, { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
  Alert,
  Snackbar,
  Card,
  Tabs,
  Tab,
  Badge,
  useTheme,
  useMediaQuery,
  Collapse,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Close as CloseIcon,
  TableView as TableViewIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  DensitySmall as DensitySmallIcon,
  Clear as ClearIcon,
  ViewList as ViewListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PrecisionManufacturing as MachineIcon,
  AccessTime as AccessTimeIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* ----------------------------- state helper ----------------------------- */
const useViewJobPlan = () => {
  const [state, setState] = useState({
    machineCode: "",
    plannedJobs: [],
    shiftLogs: [],
    lineupJobs: [],
    open: false,
    activeTab: 0,
    loading: false,
    notification: { open: false, message: "", severity: "success" },
    lastRefreshed: null,
  });
  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);
  return { state, updateState };
};




const formatName = (name, maxLength = 10) => {
  if (!name) return "";
  const trimmed = name.trim();
  return trimmed.length > maxLength
    ? trimmed.slice(0, maxLength) + "…"
    : trimmed;
};

const splitNames = (value) =>
  value
    ? value.split(",").map((n) => n.trim()).filter(Boolean)
    : [];




const TouchTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    arrow
    placement="top"
    enterTouchDelay={0}        // open immediately on touch
    leaveTouchDelay={3000}     // stay visible for 3s
    disableInteractive={false}
  >
    <span>{children}</span>
  </Tooltip>
);


const getRowKey = (row) => `${row.Employee_ID}-${row.shift_loggedIn_DateTime}`;

/* ----------------------------- TabPanel (fixed height) ----------------------------- */
function TabPanel({ children, value, index, ...other }) {
  const active = value === index;
  return (
    <Box
      role="tabpanel"
      hidden={!active}
      id={`tab-${index}`}
      {...other}
      sx={{
        flex: 1,
        minHeight: 0,
        display: active ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {children}
    </Box>
  );
}

/* ------------------------------- component ------------------------------ */
export default function ViewJobPlan({ username }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { state, updateState } = useViewJobPlan();

  // Allow multiple shifts to be expanded
  const [expandedShift, setExpandedShift] = useState({});

  // Inner sections per shift
  const [jobSectionOpen, setJobSectionOpen] = useState({});
  const [opSectionOpen, setOpSectionOpen] = useState({});

  // Cache timestamps for invalidation (5-minute TTL)
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  const [jobSummaryCache, setJobSummaryCache] = useState({});
  const [opLogCache, setOpLogCache] = useState({});
  const [cacheTimestamp, setCacheTimestamp] = useState({});
  const [jobLoading, setJobLoading] = useState({});
  const [opLoading, setOpLoading] = useState({});
  const [jobError, setJobError] = useState({});
  const [opError, setOpError] = useState({});

  // Filters & view
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dense, setDense] = useState(false);

  // Lineup tab: search filter and expanded machines
  const [lineupSearch, setLineupSearch] = useState("");
  const [expandedMachines, setExpandedMachines] = useState({});

  // Job Plan tab: expanded planning periods
  const [expandedJobPlanSections, setExpandedJobPlanSections] = useState({});

  // Derived colors/surfaces for light/dark - wrapped in useCallback to prevent recreation
  const sectionBg = useCallback(
    (t) => (t.palette.mode === "dark" ? alpha(t.palette.primary.main, 0.04) : t.palette.grey[50]),
    []
  );

  const headBgSoft = useCallback(
    (t) => (t.palette.mode === "dark" ? alpha(t.palette.text.primary, 0.08) : t.palette.grey[100]),
    []
  );

  const tableBorder = useCallback(
    (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
    []
  );

  const showNotification = useCallback(
    (message, severity = "success") => {
      updateState({ notification: { open: true, message, severity } });
    },
    [updateState]
  );

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return String(dateString);
    }
  }, []);



  const parseHHMMSSToMinutes = useCallback((hhmmss) => {
    if (!hhmmss || typeof hhmmss !== "string") return 0;
    const parts = hhmmss.split(":");
    if (parts.length !== 3) return 0;
    const [h, m, s] = parts.map((n) => Number(n || 0));
    if ([h, m, s].some((x) => Number.isNaN(x))) return 0;
    return h * 60 + m + Math.floor(s / 60);
  }, []);

  /* ----------------------------- Retry mechanism ----------------------------- */
  const fetchWithRetry = async (fetchFn, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fetchFn();
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
  };

  /* ----------------------------- fetchers ----------------------------- */
  const fetchData = useCallback(async () => {
    updateState({ loading: true });
    try {
      const mcResp = await fetchWithRetry(() =>
        axios.get(`${API_BASE_URL}/getMachineCode`, { params: { username } })
      );
      const machineCode = mcResp.data.machineCode;

      // Fetch all three tabs' data with retry
      const [jobPlanResp, shiftResp, lineupResp] = await Promise.all([
        fetchWithRetry(() =>
          axios.get(`${API_BASE_URL}/OperatorViewPlan`, {
            params: {
              machineCode,
              dateTime: new Date().toISOString().slice(0, 19).replace("T", " "),
            },
          })
        ),
        fetchWithRetry(() =>
          axios.get(`${API_BASE_URL}/shiftlogs`, {
            params: { machineCode, limit: 10 },
          })
        ),
        fetchWithRetry(() =>
          axios.get(`${API_BASE_URL}/fetchLineupJobsForGroup`, {
            params: { machineCode },
          })
        ),
      ]);

      updateState({
        machineCode,
        plannedJobs: jobPlanResp.data || [],
        shiftLogs: shiftResp.data || [],
        lineupJobs: lineupResp.data || [],
        open: true,
        loading: false,
        lastRefreshed: new Date(),
      });

      // Reset per-shift UI state
      setExpandedShift({});
      setJobSectionOpen({});
      setOpSectionOpen({});
      setJobSummaryCache({});
      setOpLogCache({});
      setCacheTimestamp({});
      setJobLoading({});
      setOpLoading({});
      setJobError({});
      setOpError({});

      // Initialize all machine sections as collapsed by default
      const machines = {};
      (lineupResp.data || []).forEach((job) => {
        if (job.machineCode) {
          machines[job.machineCode] = false; // false = collapsed by default
        }
      });
      setExpandedMachines(machines);

      // Initialize all job plan sections as collapsed by default
      const jobPlanGroups = {};


      (jobPlanResp.data || []).forEach((job) => {
        const lastUpdated = job.lastUpdated_fixed || "unknown";
        const key = `${job.planningFrom}-${job.planingTo}-${lastUpdated}`;
        jobPlanGroups[key] = false;
      });

      setExpandedJobPlanSections(jobPlanGroups);

      showNotification("Data loaded successfully!", "success");
    } catch (e) {
      console.error("fetchData error", e);
      updateState({ loading: false });
      showNotification("Failed to load data. Please try again.", "error");
    }
  }, [username, updateState, showNotification]);

  const fetchJobSummaryForShift = useCallback(
    async (row) => {
      const rowKey = getRowKey(row);
      const cacheAge = Date.now() - (cacheTimestamp[rowKey] || 0);

      // Check cache age before returning
      if (cacheAge < CACHE_DURATION && Array.isArray(jobSummaryCache[rowKey])) {
        return;
      }

      setJobLoading((prev) => ({ ...prev, [rowKey]: true }));
      setJobError((prev) => ({ ...prev, [rowKey]: "" }));

      const params = {
        machineCode: row.machineCode,
        shiftIn: row.shift_loggedIn_DateTime,
        shiftOut: row.shift_loggedOut_DateTime ?? null,
      };

      try {
        const resp = await axios.get(`${API_BASE_URL}/jobsummary/by-shift`, {
          params,
          paramsSerializer: (p) => {
            const usp = new URLSearchParams();
            Object.entries(p).forEach(([k, v]) => {
              if (v !== null && v !== undefined) usp.append(k, v);
            });
            return usp.toString();
          },
        });

        const raw = Array.isArray(resp.data) ? resp.data : [];
        // Sort by startDatetime DESC (most recent first)
        const sorted = raw.slice().sort((a, b) => {
          const am = a?.startDatetime ? new Date(a.startDatetime).getTime() : -Infinity;
          const bm = b?.startDatetime ? new Date(b.startDatetime).getTime() : -Infinity;
          return bm - am;
        });

        setJobSummaryCache((prev) => ({ ...prev, [rowKey]: sorted }));
        setCacheTimestamp((prev) => ({ ...prev, [rowKey]: Date.now() }));
        setJobLoading((prev) => ({ ...prev, [rowKey]: false }));
      } catch (err) {
        console.error("fetchJobSummaryForShift error:", err);
        setJobError((prev) => ({ ...prev, [rowKey]: "Failed to load job summary" }));
        setJobLoading((prev) => ({ ...prev, [rowKey]: false }));
      }
    },
    [jobSummaryCache, cacheTimestamp, CACHE_DURATION]
  );

  const fetchOpLogForShift = useCallback(
    async (row) => {
      const rowKey = getRowKey(row);
      const cacheAge = Date.now() - (cacheTimestamp[rowKey + "_oplog"] || 0);

      // Check cache age before returning
      if (cacheAge < CACHE_DURATION && Array.isArray(opLogCache[rowKey])) {
        return;
      }

      setOpLoading((prev) => ({ ...prev, [rowKey]: true }));
      setOpError((prev) => ({ ...prev, [rowKey]: "" }));

      const params = {
        machineCode: row.machineCode,
        shiftIn: row.shift_loggedIn_DateTime,
        shiftOut: row.shift_loggedOut_DateTime ?? null,
      };

      try {
        const resp = await axios.get(`${API_BASE_URL}/operationlog/by-shift`, {
          params,
          paramsSerializer: (p) => {
            const usp = new URLSearchParams();
            Object.entries(p).forEach(([k, v]) => {
              if (v !== null && v !== undefined) usp.append(k, v);
            });
            return usp.toString();
          },
        });

        const raw = Array.isArray(resp.data) ? resp.data : [];
        // Sort by (end_datetime || start_datetime) DESC
        const pickTime = (r) => {
          const ed = r?.end_datetime ? new Date(r.end_datetime).getTime() : -Infinity;
          const sd = r?.start_datetime ? new Date(r.start_datetime).getTime() : -Infinity;
          return Number.isFinite(ed) ? ed : sd;
        };
        const sorted = raw.slice().sort((a, b) => pickTime(b) - pickTime(a));

        setOpLogCache((prev) => ({ ...prev, [rowKey]: sorted }));
        setCacheTimestamp((prev) => ({ ...prev, [rowKey + "_oplog"]: Date.now() }));
        setOpLoading((prev) => ({ ...prev, [rowKey]: false }));
      } catch (err) {
        console.error("fetchOpLogForShift error:", err);
        setOpError((prev) => ({ ...prev, [rowKey]: "Failed to load operation log" }));
        setOpLoading((prev) => ({ ...prev, [rowKey]: false }));
      }
    },
    [opLogCache, cacheTimestamp, CACHE_DURATION]
  );

  /* ----------------------------- Clear search on tab change ----------------------------- */
  useEffect(() => {
    setSearch("");
    setLineupSearch("");
  }, [state.activeTab]);

  /* ----------------------------- Derived values for Job Plan tab ----------------------------- */


  const groupedJobs = useMemo(() => {
    const groups = {};
    state.plannedJobs.forEach((job) => {
      const lastUpdated = job.lastUpdated_fixed || "unknown";
      const key = `${job.planningFrom}-${job.planingTo}-${lastUpdated}`;

      if (!groups[key]) {
        groups[key] = {
          planningFrom: job.planningFrom,
          planingTo: job.planingTo,
          lastUpdated_fixed: job.lastUpdated_fixed,
          jobs: [],
        };
      }
      groups[key].jobs.push(job);
    });
    return groups;
  }, [state.plannedJobs]);

  const handleClose = useCallback(() => {
    updateState({ open: false });
  }, [updateState]);

  const handleRowExpand = useCallback(
    (row) => {
      const rowKey = getRowKey(row);
      const isCurrentlyExpanded = expandedShift[rowKey];

      // Close all cards and toggle the clicked one (accordion behavior)
      if (isCurrentlyExpanded) {
        // If clicking the same card, just close it
        setExpandedShift({});
      } else {
        // Open only this card, close all others
        setExpandedShift({ [rowKey]: true });

        // Fetch data for the newly expanded card
        fetchJobSummaryForShift(row);
        fetchOpLogForShift(row);
        // Default open both subtables on first expand
        setJobSectionOpen((p) => ({ ...p, [rowKey]: true }));
        setOpSectionOpen((p) => ({ ...p, [rowKey]: true }));
      }
    },
    [expandedShift, fetchJobSummaryForShift, fetchOpLogForShift]
  );

  const toggleJobSection = useCallback((row) => {
    const rowKey = getRowKey(row);
    setJobSectionOpen((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  }, []);

  const toggleOpSection = useCallback((row) => {
    const rowKey = getRowKey(row);
    setOpSectionOpen((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  }, []);

  const toggleMachineSection = useCallback((machineCode) => {
    setExpandedMachines((prev) => ({ ...prev, [machineCode]: !prev[machineCode] }));
  }, []);

  const toggleJobPlanSection = useCallback((groupKey) => {
    setExpandedJobPlanSections((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  }, []);

  /* ----------------------------- Manual Refresh Handler ----------------------------- */
  const handleManualRefresh = useCallback(() => {
    // Clear all caches
    setJobSummaryCache({});
    setOpLogCache({});
    setCacheTimestamp({});
    setExpandedShift({});
    setJobSectionOpen({});
    setOpSectionOpen({});
    setExpandedJobPlanSections({});

    // Re-fetch data
    fetchData();
  }, [fetchData]);

  /* ----------------------------- Job Plan Tab ----------------------------- */
  const renderJobPlanTab = useCallback(() => {
    if (state.loading) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
            p: 3,
          }}
        >
          <CircularProgress size={42} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Loading Job Plan...
          </Typography>
        </Box>
      );
    }

    if (Object.keys(groupedJobs).length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ color: "text.secondary" }}>
            No planned jobs available
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            p: 3,
            scrollbarGutter: "stable both-edges",
          }}
        >
          {Object.keys(groupedJobs).map((groupKey) => {
            const group = groupedJobs[groupKey];
            const isExpanded = expandedJobPlanSections[groupKey];
            const jobCount = group.jobs.length;

            return (
              <Card
                key={groupKey}
                sx={{
                  mb: 3,
                  overflow: "hidden",
                  border: tableBorder(theme),
                  backgroundColor: alpha(theme.palette.background.paper, 0.98),
                }}
              >
                <Box sx={{ p: 0 }}>
                  {/* Section header with gradient - now clickable */}
                  <Box
                    onClick={() => toggleJobPlanSection(groupKey)}
                    sx={{
                      p: 2,
                      color: theme.palette.common.white,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.common.white, 0.22)}` : 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.secondary.main, 0.9)} 100%)`,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <IconButton
                        size="small"
                        sx={{
                          color: "inherit",
                          "&:hover": { bgcolor: alpha(theme.palette.common.white, 0.1) }
                        }}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {formatDateTime(group.planningFrom)}- {formatDateTime(group.planingTo)}
                        </Typography>
                        {group.lastUpdated_fixed && (
                          <>
                            <Box
                              sx={{
                                width: 2,
                                height: 24,
                                bgcolor: alpha(theme.palette.common.white, 0.4),
                                borderRadius: 1
                              }}
                            />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <AccessTimeIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                              <Typography variant="subtitle1" sx={{ opacity: 0.95, fontWeight: 500 }}>
                                Updated: {formatDateTime(group.lastUpdated_fixed)}
                              </Typography>
                            </Box>
                          </>
                        )}

                      </Box>
                    </Box>

                    <Badge
                      badgeContent={jobCount}
                      color="secondary"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: 12,
                          height: 20,
                          minWidth: 20,
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.common.white, 0.9),
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      <Chip
                        label={`${jobCount} Job${jobCount !== 1 ? "s" : ""}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.common.white, 0.2),
                          color: "inherit",
                          fontWeight: 600,
                          "&:hover": { bgcolor: alpha(theme.palette.common.white, 0.3) },
                        }}
                      />
                    </Badge>
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <TableContainer>
                      <Table size={dense ? "small" : "medium"}>
                        <TableHead>
                          <TableRow
                            sx={{
                              backgroundColor: headBgSoft(theme),
                              "& th": { fontWeight: 700, whiteSpace: "nowrap" },
                            }}
                          >
                            {[
                              "Sequence",
                              "Job Number",
                              "Customer",
                              "Operation",
                              "WO Description",
                              "WO Qty",
                              "OP Qty",
                              "Planned Qty",
                              "Color Seq",
                              "Other Details",
                              "Setup Time",
                              "Op Time",
                              "Running Time",
                              "Time Unit",
                              "OP Start",
                              "OP End",
                              "WO Due Date",
                              "WO End Date",
                              "WO Delay",
                              "Priority",
                            ].map((h) => (
                              <TableCell key={h}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.jobs.map((job, idx) => (
                            <TableRow
                              key={`${groupKey}-${job.jobNumber}`}
                              hover
                              sx={{
                                bgcolor: idx % 2 ? "background.default" : "background.paper",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                                transition: "background-color 0.2s ease",
                              }}
                            >
                              <TableCell>{job.jobSequenceNumber}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{job.jobNumber}</TableCell>
                              <TableCell>{job.customer || "—"}</TableCell>
                              <TableCell>{job.OpDesctiption || "—"}</TableCell>
                              <TableCell sx={{ maxWidth: 250, whiteSpace: "normal", wordBreak: "break-word" }}>{job.WOdescription || "—"}</TableCell>
                              <TableCell align="right">{(job.WOQty || 0).toLocaleString()}</TableCell>
                              <TableCell align="right">{(job.OPQty || 0).toLocaleString()}</TableCell>
                              <TableCell align="right">{(job.plannedQty || 0).toLocaleString()}</TableCell>
                              <TableCell>{job.ColorSequence || "—"}</TableCell>
                              <TableCell sx={{ maxWidth: 150, whiteSpace: "normal", wordBreak: "break-word" }}>{job.OtherDetails || "—"}</TableCell>
                              <TableCell align="right">{job.SetupTime ?? "—"}</TableCell>
                              <TableCell align="right">{job.OperationalTime ?? "—"}</TableCell>
                              <TableCell align="right">{job.SetupAndOpeTime ?? "—"}</TableCell>
                              <TableCell>{job.TimeUnit || "—"}</TableCell>
                              <TableCell>{job.opstartDate || "—"}</TableCell>
                              <TableCell>{job.opendDate || "—"}</TableCell>
                              <TableCell>{job.WOduedate || "—"}</TableCell>
                              <TableCell>{job.WOenddate || "—"}</TableCell>
                              <TableCell>
                                {job.WOdelay && job.WOdelay !== "0d 0h 0m 0s" ? (
                                  <Chip
                                    label={job.WOdelay}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={job.WOpriority || "—"}
                                  size="small"
                                  color={
                                    job.WOpriority === "High"
                                      ? "error"
                                      : job.WOpriority === "Medium"
                                        ? "warning"
                                        : "default"
                                  }
                                  variant={job.WOpriority === "High" ? "filled" : "outlined"}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Box>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  }, [state.loading, groupedJobs, dense, theme, formatDateTime, headBgSoft, tableBorder, expandedJobPlanSections, toggleJobPlanSection]);

  /* ----------------------------- Shift Logs Tab (Card-based view) ----------------------------- */
  const filteredShifts = useMemo(() => {
    let filtered = state.shiftLogs;

    // Filter by status using ActiveStatus field
    if (statusFilter === "in") {
      filtered = filtered.filter((r) => r.ActiveStatus === "Logged-IN");
    } else if (statusFilter === "out") {
      filtered = filtered.filter((r) => r.ActiveStatus === "Logged-out");
    }

    if (search.trim()) {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          (r.Employee_ID && String(r.Employee_ID).toLowerCase().includes(lowerSearch)) ||
          (r.operator && r.operator.toLowerCase().includes(lowerSearch)) ||
          (r.machineCode && r.machineCode.toLowerCase().includes(lowerSearch))
      );
    }

    return filtered;
  }, [state.shiftLogs, statusFilter, search]);

  // Totals helpers for comprehensive display - wrapped in useCallback
  const sumMinutes = useCallback((jobs, field) =>
    (jobs || []).reduce((acc, cur) => acc + parseHHMMSSToMinutes(cur?.[field]), 0)
    , [parseHHMMSSToMinutes]);

  const sumNumber = useCallback((jobs, field) =>
    (jobs || []).reduce((acc, cur) => acc + Number(cur?.[field] || 0), 0)
    , []);

  const sumOpDuration = useCallback((ops) =>
    (ops || []).reduce((acc, cur) => acc + Number(cur?.duration_minutes || 0), 0)
    , []);

  const sumOpProduction = useCallback((ops) =>
    (ops || []).reduce((acc, cur) => {
      const t = String(cur?.operation_type || "").toLowerCase();
      return acc + (t === "production" ? Number(cur?.duration_minutes || 0) : 0);
    }, 0)
    , []);

  const renderShiftLogsTab = useCallback(() => {
    // Section Header component for collapsible sections - defined inside render function
    const SectionHeader = ({ title, count, rightChips, isOpen, onToggle }) => (
      <Box
        sx={{
          py: 1,
          px: 1.5,
          borderRadius: 1,
          bgcolor: "background.paper",
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onClick={onToggle}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" aria-label={isOpen ? "Collapse" : "Expand"}>
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Chip size="small" label={`${count} item${count !== 1 ? "s" : ""}`} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>{rightChips}</Box>
      </Box>
    );
    const toolbar = (
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search (ID, name, machine)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: 1 }}
        />

        <Chip
          label={`${filteredShifts.length} Shift${filteredShifts.length !== 1 ? "s" : ""}`}
          size="small"
          color="default"
          variant="outlined"
        />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, val) => val && setStatusFilter(val)}
        >
          <ToggleButton value="all">
            <FilterListIcon sx={{ mr: 0.5 }} fontSize="small" /> All
          </ToggleButton>
          <ToggleButton value="in">Logged-IN</ToggleButton>
          <ToggleButton value="out">Logged-out</ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title={dense ? "Comfortable spacing" : "Compact density"}>
          <IconButton size="small" onClick={() => setDense((d) => !d)}>
            <DensitySmallIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchData} disabled={state.loading}>
            {state.loading ? <CircularProgress size={18} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    );

    if (state.loading) {
      return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {toolbar}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              p: 3,
            }}
          >
            <CircularProgress size={42} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading Shift Logs...
            </Typography>
          </Box>
        </Box>
      );
    }

    if (!filteredShifts.length) {
      return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {toolbar}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ color: "text.secondary" }}>
              No shifts found
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {toolbar}

        {/* Card-based shift list with scroll */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            // Ensure smooth scrolling
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": {
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: alpha(theme.palette.divider, 0.1),
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.primary.main, 0.3),
              borderRadius: 4,
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.5),
              },
            },
          }}
        >
          {filteredShifts.map((row) => {
            const rowKey = getRowKey(row);
            const isExpanded = !!expandedShift[rowKey];
            const isJobOpen = !!jobSectionOpen[rowKey];
            const isOpOpen = !!opSectionOpen[rowKey];

            const jobCache = jobSummaryCache[rowKey] || [];
            const opCache = opLogCache[rowKey] || [];
            const jobLoadingState = jobLoading[rowKey];
            const opLoadingState = opLoading[rowKey];
            const jobErr = jobError[rowKey];
            const opErr = opError[rowKey];

            const isActive = row.ActiveStatus === "Logged-IN";

            // Shift-level totals
            const totalProdMin = sumMinutes(jobCache, "totalProductionTime");
            const totalSetupMin = sumMinutes(jobCache, "totalSetupTime");
            const totalIdleMin = sumMinutes(jobCache, "totalIdleTime");
            const totalMealsMin = sumMinutes(jobCache, "totalMealsTime");
            const totalProdQty = sumNumber(jobCache, "productionQty");
            const totalWasteQty = sumNumber(jobCache, "wasteQty");
            const totalOpMin = sumOpDuration(opCache);
            const totalOpProdMin = sumOpProduction(opCache);

            return (
              <Card
                key={rowKey}
                sx={{
                  overflow: "hidden",
                  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                  borderLeft: isActive
                    ? `4px solid ${theme.palette.success.main}`
                    : `4px solid ${theme.palette.grey[400]}`,
                  borderRadius: 2,
                  transition: "all 0.2s ease",
                  flexShrink: 0, // Prevent cards from shrinking
                  "&:hover": {
                    boxShadow: 4,
                  },
                }}
              >
                {/* Card Header - Clickable to expand */}
                <Box
                  onClick={() => handleRowExpand(row)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    bgcolor: isActive
                      ? alpha(theme.palette.success.main, 0.06)
                      : alpha(theme.palette.grey[500], 0.04),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: isActive
                        ? alpha(theme.palette.success.main, 0.1)
                        : alpha(theme.palette.grey[500], 0.08),
                    },
                  }}
                >
                  {/* Left side - Main shift info */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>

                    {/* Shift Times - Primary Display */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>


                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                          {/* Main Operator */}
                          <TouchTooltip title={row.operator || "Unknown Operator"}>
                            <Chip
                              size="small"
                              label={formatName(row.operator || "Unknown")}
                              color="primary"
                              sx={{ fontWeight: 600 }}
                            />
                          </TouchTooltip>

                          {/* Assistant Operators */}
                          {splitNames(row.AstOperator).map((name, idx) => (
                            <TouchTooltip key={`ast-${idx}`} title={name}>
                              <Chip
                                size="small"
                                label={`Ast: ${formatName(name)}`}
                                variant="outlined"
                                color="info"
                              />
                            </TouchTooltip>
                          ))}

                          {/* Helpers */}
                          {splitNames(row.Helpers).map((name, idx) => (
                            <TouchTooltip key={`helper-${idx}`} title={name}>
                              <Chip
                                size="small"
                                label={`Helper: ${formatName(name)}`}
                                variant="outlined"
                                color="default"
                              />
                            </TouchTooltip>
                          ))}
                        </Box>



                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          ({row.Employee_ID || "—"})
                        </Typography>
                        <Chip
                          size="small"
                          label={row.ActiveStatus || "—"}
                          color={isActive ? "success" : "default"}
                          variant={isActive ? "filled" : "outlined"}
                          sx={{ fontWeight: 600, height: 20, fontSize: "0.7rem" }}
                        />
                      </Box>

                      {/* Date/Time Display - Single Line */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 14, color: "success.main" }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "success.main" }}>
                            IN:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.primary" }}>
                            {formatDateTime(row.shift_loggedIn_DateTime)}
                          </Typography>
                        </Box>

                        <Typography variant="caption" sx={{ color: "text.disabled" }}>|</Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 14, color: row.shift_loggedOut_DateTime ? "error.main" : "text.disabled" }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: row.shift_loggedOut_DateTime ? "error.main" : "text.disabled" }}>
                            OUT:
                          </Typography>
                          <Typography variant="caption" sx={{ color: row.shift_loggedOut_DateTime ? "text.primary" : "text.disabled" }}>
                            {row.shift_loggedOut_DateTime ? formatDateTime(row.shift_loggedOut_DateTime) : "Still Active"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Right side - Machine code */}
                  <Chip
                    icon={<MachineIcon sx={{ fontSize: 16 }} />}
                    label={row.machineCode || "—"}
                    variant="outlined"
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600, height: 26 }}
                  />
                </Box>

                {/* Expanded Content */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      bgcolor: theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.main, 0.04)
                        : theme.palette.grey[50],
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    }}
                  >
                    {/* Shift Details Section */}
                    <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: "primary.main" }}>
                        Shift Details
                      </Typography>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Employee ID</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.Employee_ID || "—"}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Operator</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.operator || "—"}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Machine</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.machineCode || "—"}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Shift In</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDateTime(row.shift_loggedIn_DateTime)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Shift Out</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.shift_loggedOut_DateTime ? formatDateTime(row.shift_loggedOut_DateTime) : "—"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Box>
                            <Chip
                              size="small"
                              label={row.ActiveStatus || "—"}
                              color={isActive ? "success" : "default"}
                              variant={isActive ? "filled" : "outlined"}
                            />
                          </Box>
                        </Box>
                        {row.remarks && (
                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <Typography variant="caption" color="text.secondary">Remarks</Typography>
                            <Typography variant="body2">{row.remarks}</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Job Summary Section */}
                    <Box sx={{ p: 2 }}>
                      <SectionHeader
                        title="Job Summary"
                        count={jobCache.length}
                        isOpen={isJobOpen}
                        onToggle={(e) => {
                          e.stopPropagation();
                          toggleJobSection(row);
                        }}
                        rightChips={
                          <>
                            <Chip size="small" color="primary" label={`Prod min: ${totalProdMin}`} />
                            <Chip size="small" label={`Setup: ${totalSetupMin}`} />
                            <Chip size="small" label={`Idle: ${totalIdleMin}`} />
                            <Chip size="small" label={`Meals: ${totalMealsMin}`} />
                            <Chip size="small" color="success" label={`Prod Qty: ${totalProdQty}`} />
                            <Chip size="small" color="error" label={`Waste: ${totalWasteQty}`} />
                          </>
                        }
                      />

                      <Collapse in={isJobOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 1 }}>
                          {jobLoadingState && (
                            <Box display="flex" alignItems="center" gap={1} sx={{ p: 2 }}>
                              <CircularProgress size={18} />
                              <Typography variant="body2">Loading jobs…</Typography>
                            </Box>
                          )}
                          {jobErr && (
                            <Alert severity="error" sx={{ my: 1 }}>
                              {jobErr}
                            </Alert>
                          )}
                          {!jobLoadingState && !jobErr && jobCache.length === 0 && (
                            <Box sx={{ p: 2, color: "text.secondary" }}>
                              No jobs found for this shift.
                            </Box>
                          )}
                          {!jobLoadingState && !jobErr && jobCache.length > 0 && (
                            <TableContainer sx={{ overflowX: "auto" }}>
                              <Table size={dense ? "small" : "medium"} sx={{ minWidth: 2200 }}>
                                <TableHead>
                                  <TableRow
                                    sx={{
                                      bgcolor: theme.palette.primary.dark,
                                      "& th": { color: "white", fontWeight: 700, whiteSpace: "nowrap" },
                                    }}
                                  >
                                    <TableCell>Started</TableCell>
                                    <TableCell>Ended</TableCell>
                                    <TableCell>Job #</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Prod Qty</TableCell>
                                    <TableCell>Waste Qty</TableCell>
                                    <TableCell>Finished Good</TableCell>
                                    <TableCell>Close Remarks</TableCell>
                                    <TableCell>Setup Time</TableCell>
                                    <TableCell>Idle Time</TableCell>
                                    <TableCell>Production Time</TableCell>
                                    <TableCell>Meals Time</TableCell>
                                    <TableCell>Idle Reasons</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Avg Prod Speed</TableCell>
                                    <TableCell align="right">Avg Op Speed</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {jobCache.map((js, i) => (
                                    <TableRow
                                      key={`${rowKey}-job-${i}`}
                                      hover
                                      sx={{ bgcolor: i % 2 ? "background.default" : "background.paper" }}
                                    >
                                      <TableCell>{formatDateTime(js.startDatetime)}</TableCell>
                                      <TableCell>{js.endDatetime ? formatDateTime(js.endDatetime) : "—"}</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>
                                        {js.jobNo || "—"}
                                      </TableCell>

                                      <TableCell>{js.customer || "—"}</TableCell>
                                      <TableCell>{js.product || "—"}</TableCell>
                                      <TableCell align="right">{js.productionQty || 0}</TableCell>
                                      <TableCell align="right">{js.wasteQty || 0}</TableCell>
                                      <TableCell>{js.finishedGood || "—"}</TableCell>
                                      <TableCell sx={{ maxWidth: 480, whiteSpace: "normal", wordBreak: "break-word" }}>
                                        {js.jobCloseremarks || "—"}
                                      </TableCell>
                                      <TableCell>{js.totalSetupTime || "00:00:00"}</TableCell>
                                      <TableCell>{js.totalIdleTime || "00:00:00"}</TableCell>
                                      <TableCell>{js.totalProductionTime || "00:00:00"}</TableCell>
                                      <TableCell>{js.totalMealsTime || "00:00:00"}</TableCell>
                                      <TableCell sx={{ maxWidth: 480, whiteSpace: "normal", wordBreak: "break-word" }}>
                                        {js.idleReasons || "—"}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={js.status || "—"}
                                          size="small"
                                          color={String(js.status).toLowerCase() === "finished" ? "success" : "default"}
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        {js.avg_prod_speed !== undefined && js.avg_prod_speed !== null
                                          ? Number(js.avg_prod_speed).toFixed(2)
                                          : "0.00"}
                                      </TableCell>
                                      <TableCell align="right">
                                        {js.avg_op_speed !== undefined && js.avg_op_speed !== null
                                          ? Number(js.avg_op_speed).toFixed(2)
                                          : "0.00"}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Box>
                      </Collapse>

                      {/* Operation Log Section */}
                      <Box sx={{ mt: 2 }}>
                        <SectionHeader
                          title="Operation Logs"
                          count={opCache.length}
                          isOpen={isOpOpen}
                          onToggle={(e) => {
                            e.stopPropagation();
                            toggleOpSection(row);
                          }}
                          rightChips={
                            <>
                              <Chip size="small" color="primary" label={`Prod min: ${totalOpProdMin}`} />
                              <Chip size="small" label={`Total min: ${totalOpMin}`} />
                            </>
                          }
                        />
                        <Collapse in={isOpOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ mt: 1 }}>
                            {opLoadingState && (
                              <Box display="flex" alignItems="center" gap={1} sx={{ p: 2 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2">Loading operations…</Typography>
                              </Box>
                            )}
                            {opErr && (
                              <Alert severity="error" sx={{ my: 1 }}>
                                {opErr}
                              </Alert>
                            )}
                            {!opLoadingState && !opErr && opCache.length === 0 && (
                              <Box sx={{ p: 2, color: "text.secondary" }}>
                                No operations found for this shift.
                              </Box>
                            )}
                            {!opLoadingState && !opErr && opCache.length > 0 && (
                              <TableContainer sx={{ overflowX: "auto" }}>
                                <Table size={dense ? "small" : "medium"} sx={{ minWidth: 1400 }}>
                                  <TableHead>
                                    <TableRow
                                      sx={{
                                        bgcolor: theme.palette.info.dark,
                                        "& th": { color: "white", fontWeight: 700, whiteSpace: "nowrap" },
                                      }}
                                    >
                                      <TableCell>Start</TableCell>
                                      <TableCell>End</TableCell>
                                      <TableCell>Job Started</TableCell>
                                      <TableCell>Job #</TableCell>
                                      <TableCell>Customer</TableCell>
                                      <TableCell>Product</TableCell>
                                      <TableCell>Main Op</TableCell>
                                      <TableCell>Op Code</TableCell>
                                      <TableCell>Op Type</TableCell>
                                      <TableCell>Description</TableCell>
                                      <TableCell>Remarks</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell align="right">Duration</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {opCache.map((op, i) => (
                                      <TableRow
                                        key={`${rowKey}-op-${i}`}
                                        hover
                                        sx={{ bgcolor: i % 2 ? "background.default" : "background.paper" }}
                                      >
                                        <TableCell>{formatDateTime(op.start_datetime)}</TableCell>
                                        <TableCell>{op.end_datetime ? formatDateTime(op.end_datetime) : "—"}</TableCell>
                                        <TableCell>{op.JobStartedTime ? formatDateTime(op.JobStartedTime) : "—"}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{op.job_number || "—"}</TableCell>
                                        <TableCell sx={{ maxWidth: 240, whiteSpace: "normal", wordBreak: "break-word" }}>
                                          {op.customer || "—"}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 240, whiteSpace: "normal", wordBreak: "break-word" }}>
                                          {op.product || "—"}
                                        </TableCell>
                                        <TableCell>{op.main_operation || "—"}</TableCell>
                                        <TableCell>{op.operation_code || "—"}</TableCell>
                                        <TableCell>{op.operation_type || "—"}</TableCell>
                                        <TableCell sx={{ maxWidth: 320, whiteSpace: "normal", wordBreak: "break-word" }}>
                                          {op.operation_description || "—"}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 480, whiteSpace: "normal", wordBreak: "break-word" }}>
                                          {op.remarks || "—"}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={op.active_status || "—"}
                                            size="small"
                                            color={op.active_status === "Finished" ? "success" : "default"}
                                          />
                                        </TableCell>
                                        <TableCell align="right">
                                          {(() => {
                                            if (typeof op?.duration_minutes === "number" && typeof op?.duration_hours === "number") {
                                              const min = Math.round(op.duration_minutes);
                                              const hrs = op.duration_hours.toFixed(2);
                                              return `${min} (${hrs} hrs)`;
                                            }
                                            if (op?.duration && typeof op.duration === "string") {
                                              const [h = 0, m = 0, s = 0] = op.duration.split(":").map(Number);
                                              const minutes = h * 60 + m + Math.floor((s || 0) / 60);
                                              const hours = (minutes / 60).toFixed(2);
                                              return `${minutes} (${hours} hrs)`;
                                            }
                                            const s = op?.start_datetime ? new Date(op.start_datetime) : null;
                                            const e = op?.end_datetime ? new Date(op.end_datetime) : null;
                                            const minutes = s && e ? Math.max(0, Math.round((e - s) / 60000)) : 0;
                                            const hours = (minutes / 60).toFixed(2);
                                            return `${minutes} (${hours} hrs)`;
                                          })()}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  }, [
    state.loading,
    filteredShifts,
    search,
    statusFilter,
    dense,
    expandedShift,
    jobSectionOpen,
    opSectionOpen,
    jobSummaryCache,
    opLogCache,
    jobLoading,
    opLoading,
    jobError,
    opError,
    theme,
    formatDateTime,
    fetchData,
    handleRowExpand,
    toggleJobSection,
    toggleOpSection,
    sumMinutes,
    sumNumber,
    sumOpDuration,
    sumOpProduction,
  ]);

  /* ----------------------------- Lineup Tab with Machine Grouping ----------------------------- */

  // Group lineup jobs by machineCode
  const groupedLineupJobs = useMemo(() => {
    const groups = {};

    state.lineupJobs.forEach((job) => {
      const mc = job.machineCode || "Unknown";
      if (!groups[mc]) {
        groups[mc] = [];
      }
      groups[mc].push(job);
    });

    // Sort jobs within each group by jobSequenceNumber
    Object.keys(groups).forEach((mc) => {
      groups[mc].sort((a, b) => {
        const seqA = a.jobSequenceNumber || 0;
        const seqB = b.jobSequenceNumber || 0;
        return seqA - seqB;
      });
    });

    return groups;
  }, [state.lineupJobs]);

  // Filter grouped jobs by search
  const filteredGroupedLineupJobs = useMemo(() => {
    if (!lineupSearch.trim()) return groupedLineupJobs;

    const lowerSearch = lineupSearch.toLowerCase();
    const filteredGroups = {};

    Object.entries(groupedLineupJobs).forEach(([machineCode, jobs]) => {
      const filteredJobs = jobs.filter(
        (job) =>
          (job.jobNumber && job.jobNumber.toLowerCase().includes(lowerSearch)) ||
          (job.customer && job.customer.toLowerCase().includes(lowerSearch)) ||
          (job.WOdescription && job.WOdescription.toLowerCase().includes(lowerSearch)) ||
          (job.OpDesctiption && job.OpDesctiption.toLowerCase().includes(lowerSearch)) ||
          (machineCode && machineCode.toLowerCase().includes(lowerSearch))
      );

      if (filteredJobs.length > 0) {
        filteredGroups[machineCode] = filteredJobs;
      }
    });

    return filteredGroups;
  }, [groupedLineupJobs, lineupSearch]);

  const renderLineupTab = useCallback(() => {
    const machineCodeKeys = Object.keys(filteredGroupedLineupJobs).sort();
    const totalJobs = Object.values(filteredGroupedLineupJobs).reduce(
      (sum, jobs) => sum + jobs.length,
      0
    );

    const toolbar = (
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: sectionBg(theme),
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search lineup jobs or machine code..."
          value={lineupSearch}
          onChange={(e) => setLineupSearch(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: lineupSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setLineupSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />

        <Chip
          icon={<MachineIcon fontSize="small" />}
          label={`${machineCodeKeys.length} Machine${machineCodeKeys.length !== 1 ? "s" : ""}`}
          size="small"
          color="primary"
          variant="outlined"
        />

        <Chip
          label={`${totalJobs} Total Job${totalJobs !== 1 ? "s" : ""}`}
          size="small"
          color="info"
          variant="outlined"
        />

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchData}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );

    if (state.loading) {
      return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {toolbar}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              p: 3,
            }}
          >
            <CircularProgress size={42} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading Lineup Jobs...
            </Typography>
          </Box>
        </Box>
      );
    }

    if (machineCodeKeys.length === 0) {
      return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {toolbar}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ color: "text.secondary" }}>
              {lineupSearch ? "No matching lineup jobs found" : "No lineup jobs available"}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {toolbar}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            px: 1.5,
            py: 1,
          }}
        >
          {machineCodeKeys.map((machineCode) => {
            const jobs = filteredGroupedLineupJobs[machineCode];
            const isExpanded = expandedMachines[machineCode];
            const isCurrentMachine = machineCode === state.machineCode;

            return (
              <Card
                key={machineCode}
                sx={{
                  mb: 1.5,
                  border: tableBorder(theme),
                  borderRadius: 2,
                  background: alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: "blur(6px)",
                  boxShadow: isCurrentMachine ? 3 : 1,
                  borderLeft: isCurrentMachine ? `4px solid ${theme.palette.primary.main}` : undefined,
                }}
              >
                {/* Machine Header - Collapsible */}
                <Box
                  onClick={() => toggleMachineSection(machineCode)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    bgcolor: alpha(
                      isCurrentMachine ? theme.palette.primary.main : theme.palette.info.main,
                      0.08
                    ),
                    borderBottom: isExpanded ? 1 : 0,
                    borderColor: "divider",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: alpha(
                        isCurrentMachine ? theme.palette.primary.main : theme.palette.info.main,
                        0.15
                      ),
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>

                    <MachineIcon
                      sx={{
                        color: isCurrentMachine
                          ? theme.palette.primary.main
                          : theme.palette.info.main,
                      }}
                    />

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: isCurrentMachine
                          ? theme.palette.primary.main
                          : theme.palette.info.main,
                      }}
                    >
                      {machineCode}
                    </Typography>

                    {isCurrentMachine && (
                      <Chip
                        label="Your Machine"
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 600, height: 22 }}
                      />
                    )}
                  </Box>

                  <Badge
                    badgeContent={jobs.length}
                    color={isCurrentMachine ? "primary" : "info"}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: 12,
                        height: 20,
                        minWidth: 20,
                        fontWeight: 700,
                      },
                    }}
                  >
                    <Chip
                      label={`${jobs.length} Job${jobs.length !== 1 ? "s" : ""}`}
                      size="small"
                      variant="outlined"
                      color={isCurrentMachine ? "primary" : "info"}
                    />
                  </Badge>
                </Box>

                {/* Jobs Table - Collapsible */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>
                            Seq
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>
                            Job No
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>
                            Customer
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>
                            Product
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>
                            Operation
                          </TableCell>
                          <TableCell>Color Seq</TableCell>      {/* NEW */}
                          <TableCell>Other Details</TableCell>  {/* NEW */}
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>
                            Planned Qty
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>
                            Op Time (hrs)
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>
                            Setup (hrs)
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>
                            Start Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>
                            End Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>WO Qty</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>Due Date</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>Priority</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: headBgSoft(theme) }}>Time Unit</TableCell>

                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobs.map((job, idx) => (
                          <TableRow
                            key={idx}
                            sx={{
                              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                              transition: "background-color 0.2s ease",
                            }}
                          >
                            <TableCell>
                              <Chip
                                label={job.jobSequenceNumber || "—"}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  minWidth: 32,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{job.jobNumber || "—"}</TableCell>
                            <TableCell>{job.customer || "—"}</TableCell>
                            <TableCell>{job.WOdescription || "—"}</TableCell>
                            <TableCell>{job.OpDesctiption || "—"}</TableCell>
                            <TableCell>{job.ColorSequence || "—"}</TableCell>   {/* NEW */}
                            <TableCell>{job.OtherDetails || "—"}</TableCell>    {/* NEW */}
                            <TableCell>{job.plannedQty || "—"}</TableCell>
                            <TableCell>{job.OperationalTime || "—"}</TableCell>
                            <TableCell>{job.SetupTime || "—"}</TableCell>
                            <TableCell>{job.opstartDate}</TableCell>
                            <TableCell>{job.opendDate}</TableCell>
                            <TableCell>{job.WOQty || "—"}</TableCell>
                            <TableCell>{job.WOduedate}</TableCell>
                            <TableCell>
                              <Chip
                                label={job.WOpriority || "—"}
                                size="small"
                                color={
                                  job.WOpriority === "High"
                                    ? "error"
                                    : job.WOpriority === "Medium"
                                      ? "warning"
                                      : "default"
                                }
                              />
                            </TableCell>
                            <TableCell>{job.TimeUnit || "—"}</TableCell>

                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  }, [
    state.loading,
    state.machineCode,
    filteredGroupedLineupJobs,
    lineupSearch,
    expandedMachines,
    theme,
    fetchData,
    toggleMachineSection,
    sectionBg,
    headBgSoft,
    tableBorder,
  ]);

  /* --------------------------------- shell -------------------------------- */
  return (
    <Box>
      <Tooltip title="View job plan, lineup & shifts" arrow>
        <Button
          variant="contained"
          color="primary"
          onClick={fetchData}
          disabled={state.loading}
          startIcon={state.loading ? <CircularProgress size={16} /> : <TableViewIcon />}
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 1.5,
            boxShadow: 3,
            "&:hover": { boxShadow: 6, transform: "translateY(-1px)" },
            transition: "all 0.2s ease-in-out",
          }}
        >
          {state.loading ? "Loading..." : "View Plan/Lineup/Completed"}
        </Button>
      </Tooltip>

      <Dialog
        open={state.open}
        onClose={handleClose}
        fullScreen={isMobile}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            width: "96vw",
            maxWidth: "96vw",
            height: "96vh",
            maxHeight: "96vh",
            m: 0,
            display: "flex",
            flexDirection: "column",
            background: alpha(theme.palette.background.default, 0.98),
            border: tableBorder(theme),
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            p: 1,
            flex: "0 0 auto",
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: "blur(6px)",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1.5}
            flexWrap="wrap"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
                flex: 1,
              }}
            >


              {state.machineCode && (
                <Chip
                  label={`Machine: ${state.machineCode}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ maxWidth: 260 }}
                />
              )}

              {/* Last Refreshed Timestamp */}
              {state.lastRefreshed && (
                <Chip
                  icon={<AccessTimeIcon fontSize="small" />}
                  label={`Updated: ${state.lastRefreshed.toLocaleTimeString()}`}
                  size="small"
                  variant="outlined"
                  color="default"
                  sx={{ fontSize: "0.75rem" }}
                />
              )}

              <Tabs
                value={state.activeTab}
                onChange={(_, v) => updateState({ activeTab: v })}
                variant={isMobile ? "scrollable" : "standard"}
                allowScrollButtonsMobile
                sx={{
                  ml: 1,
                  minHeight: 36,
                  "& .MuiTabs-indicator": { height: 2 },
                  "& .MuiTab-root": { minHeight: 36, px: 2 },
                }}
              >
                <Tab
                  icon={<ScheduleIcon fontSize="small" />}
                  iconPosition="start"
                  label={
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Job Plan
                      </Typography>
                      <Badge
                        badgeContent={state.plannedJobs.length}
                        color="primary"
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: 11,
                            height: 20,
                            minWidth: 20,
                            fontWeight: 700
                          }
                        }}
                      />
                    </Box>
                  }
                />
                <Tab
                  icon={<CheckCircleIcon fontSize="small" />}
                  iconPosition="start"
                  label={
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Recent Shifts
                      </Typography>
                      <Badge
                        badgeContent={state.shiftLogs.length}
                        color="success"
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: 11,
                            height: 20,
                            minWidth: 20,
                            fontWeight: 700
                          }
                        }}
                      />
                    </Box>
                  }
                />
                <Tab
                  icon={<ViewListIcon fontSize="small" />}
                  iconPosition="start"
                  label={
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Lineup
                      </Typography>
                      <Badge
                        badgeContent={state.lineupJobs.length}
                        color="info"
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: 11,
                            height: 20,
                            minWidth: 20,
                            fontWeight: 700
                          }
                        }}
                      />
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {/* Manual Refresh Button */}
              <Tooltip title="Refresh all data" arrow>
                <IconButton
                  onClick={handleManualRefresh}
                  disabled={state.loading}
                  size="small"
                  sx={{
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  {state.loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>

              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            backgroundColor: alpha(theme.palette.background.default, 0.96),
          }}
        >
          <TabPanel value={state.activeTab} index={0}>
            {renderJobPlanTab()}
          </TabPanel>
          <TabPanel value={state.activeTab} index={1}>
            {renderShiftLogsTab()}
          </TabPanel>
          <TabPanel value={state.activeTab} index={2}>
            {renderLineupTab()}
          </TabPanel>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={state.notification.open}
        autoHideDuration={5000}
        onClose={() =>
          updateState({ notification: { ...state.notification, open: false } })
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            updateState({ notification: { ...state.notification, open: false } })
          }
          severity={state.notification.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {state.notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

ViewJobPlan.propTypes = {
  username: PropTypes.string.isRequired,
};